import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { POST_INPUTS } from '../../constants';
import {
  MDBCol, MDBIcon,
  MDBRow,
} from 'mdb-react-ui-kit';
import { ApiClient } from '../../api_client';
import './form_builder.css';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch, useSelector } from 'react-redux';
import { getUser, getUserId, updateUserTokens } from '../../store/user.reducer';
import { useTranslation } from 'react-i18next';
import FieldsCreator from '../fields_creator/fields_creator';
import TopicsList from '../topics_list/topics_list';
import { Button, Col, Row, Radio, Badge } from 'antd';
import Suggestions from '../suggestions/suggestions';
import PostAutomation from '../post_automation/post_automation';

const FormBuilder = (props: any) => {

  const userId = useSelector(getUserId);
  const user = useSelector(getUser);
  const {t} = useTranslation();

  const dispatch = useDispatch();

  const [promptMode, setPromptMode]: [any, any] = useState('prompt');
  const [formData, setFormData]: [any, any] = useState({});
  const [topic, setTopic]: [any, any] = useState('');
  const [pendingRequest, setPendingRequest]: [boolean, Dispatch<SetStateAction<boolean>>] = useState(false)
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [automatedMode, setAutomatedMode] = useState(undefined);

  const toggleShow = () => setShowAdvancedSettings(!showAdvancedSettings);
  const postsInputs: any = POST_INPUTS;

  useEffect(() => {
    setTopic(user.profile?.topic);
    updateInputData('outputPostLanguage', user.profile?.outputPostLanguage, 'advancedFields');
  }, [user?.profile]);

  useEffect(() => {
    setAutomatedMode(user?.automation);
  }, [user?.automation]);

  useEffect(() => {
    setFormData((data: any) => ({
        ...data,
        socialAccounts: props.socialAccounts,
      })
    )
  }, [props.socialAccounts]);

  const updateInputData = (name: string, value: any, fieldGroup: string, isSensitive: boolean = false) => {

    // If value is empty, completely remove the field from formData state
    setFormData((data: any) => {
      if (value === '') {
        delete data[fieldGroup][name]
        return {...data};
      } else {
        return {
          ...data,
          [fieldGroup]: {
            ...data[fieldGroup],
            [name]: {
              value,
              isSensitive
            }
          }
        }
      }
    })
  }

  const handleInputChange = (event: any, fieldGroup: string, isSensitive: boolean = false) => {
    const {name, value} = event.target;
    updateInputData(name, value, fieldGroup, isSensitive);
  };

  const handleSubmit = (event: any) => {
    // TODO Refactor this

    const {socialAccounts, mainFields, advancedFields} = formData;

    // Remove brand related fields
    const {brandLogo, primaryColor, secondaryColor, ...filteredAdvancedFields} = advancedFields;
    props.pendingRequest(true);
    setPendingRequest(true);
    ApiClient.generatePost({
      user: userId,
      topic,
      socialAccounts: Object.keys(socialAccounts).filter(key => socialAccounts[key]),
      ...mainFields,
      ...filteredAdvancedFields
    }).then((response: any) => {
      if (response.data?.post == null) {
        handlePostGenerationError(null);
      }
      dispatch(updateUserTokens({
        tokens: response?.data?.remainingTokens,
      }))
      props.updateGeneratedMessage({
        ...response?.data,
        suggestedImageUrl: formData?.mainFields?.imageUrl
      });
    })
      .catch((error) => {
        const data = error.response?.data;
        handlePostGenerationError(data.errorCode);
      })
      .finally(() => {
        props.pendingRequest(false);
        setPendingRequest(false);
      });
    event.preventDefault();
  };

  const handlePostGenerationError = (errorCode: string | null) => {
    let errorMessage = '';
    switch (errorCode) {
      case 'TRIAL_EXPIRED':
        errorMessage = t('shared.errors.trialExpired');
        break;
      case 'NO_MORE_TOKENS':
        errorMessage = t('shared.errors.tokensExpired');
        break;
      case 'MEMBERSHIP_EXPIRED':
        errorMessage = t('shared.errors.membershipExpired');
        break;
      default:
        errorMessage = t('shared.errors.unexpected');
    }
    toast.error(errorMessage);
  }

  const updateFormDataFromSuggestions = ({topic, title, description, content, imageUrl}: any) => {
    setTopic(topic);
    setFormData({
      ...formData,
      mainFields: {
        title: {
          value: title
        },
        description: {
          value: description
        },
        content: {
          value: content
        },
        imageUrl
      }
    })
  }

  const mainFormBuilder = () => {
    let renderer = [];

    if (promptMode === 'suggestion') {
      return (<Suggestions updateFormData={updateFormDataFromSuggestions}/>)
    }

    if (promptMode === 'automation') {
      return (<PostAutomation automatedMode={setAutomatedMode}/>)
    }

    if (promptMode === 'prompt') {
      renderer.push(
        <MDBRow className="row-cols-sm-2 mt-4" key="topic-params">
          <MDBCol>
            <TopicsList propagateTopic={(newTopic: string) => {
              setFormData((data: any) => {
                const {mainFields, ...cleanedData} = data;
                return {
                  ...cleanedData,
                }
              });
              setTopic(newTopic);
            }}/>
          </MDBCol>
          <MDBCol>
            <select className="w-100" name="outputPostLanguage"
                    defaultValue=""
                    value={formData.advancedFields?.outputPostLanguage?.value || ''}
                    onChange={(event) => {
                      handleInputChange(event, 'advancedFields')
                    }}>
              <option value="" disabled>
                {t('newPost.chooseLanguage')}
              </option>
              <option value="english">{t(`shared.english`)}</option>
              <option value="french">{t(`shared.french`)}</option>
              <option value="arabic">{t(`shared.arabic`)}</option>
              <option value="spanish">{t(`shared.spanish`)}</option>
              <option value="german">{t(`shared.german`)}</option>
              <option value="italian">{t(`shared.italian`)}</option>
            </select>
          </MDBCol>
        </MDBRow>
      )

      if (topic) {
        renderer.push(
          <MDBRow className="row-cols-sm-2" key="subtopic-params">
            {<FieldsCreator fields={postsInputs.mainFields[topic]} fieldsGroup="mainFields"
                            propagateFormData={(data: any) => setFormData((d: any) => ({...d, ...data}))}
                            propagateInputData={updateInputData}
            />}
          </MDBRow>
        )
      }
    }

    return renderer;

  }

  const switchPromptMode = (value: string) => {
    setPromptMode(value);
    // Set back user preferred topicf from their profile on manualMode
    if (!value) {
      setTopic(user.profile?.topic);
    }
  }

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Row justify="center" className="mt-4">
          <Col className="auto-switch-wrapper">
            <Radio.Group
              value={promptMode}
              onChange={(e: any) => switchPromptMode(e.target.value)}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="prompt">Prompt</Radio.Button>
              <Radio.Button value="suggestion">Suggestion</Radio.Button>
              <Radio.Button value="automation">
                Automation
                {
                  automatedMode !== undefined &&
                  <Badge
                    className={`site-badge-count-109 position-absolute`}
                    count={automatedMode ? 'ON' : 'OFF'}
                    offset={[5, -10]}
                    style={{backgroundColor: automatedMode ? 'green' : 'red'}}
                  />
                }
              </Radio.Button>
            </Radio.Group>
          </Col>
        </Row>

        {mainFormBuilder()}

        {promptMode !== 'automation' &&
          <>
            <div className="more-settings mt-4">
              <Button
                type="default"
                size="small"
                onClick={toggleShow}
                className="p-3 bg-white"
              >
                <MDBIcon fas icon="cog"/>
                {t('newPost.advancedSettings')}
              </Button>
            </div>


            <div className={`overflow-hidden ${showAdvancedSettings ? 'h-100' : 'h-0'}`}>
              <Row gutter={[16, 16]}>
                <FieldsCreator
                  fields={postsInputs.advancedFields}
                  fieldsGroup="advancedFields"
                  propagateFormData={(data: any) => setFormData((d: any) => ({...d, ...data}))}
                  propagateInputData={updateInputData}
                />
              </Row>
            </div>
            <Row gutter={[16, 16]} className="mt-4">
              <Col span={24}>
                <Button
                  id="generate-post"
                  className="btn-with-loader w-100 fw-bold text-center p-2"
                  size="large"
                  shape="round"
                  type="primary"
                  loading={pendingRequest ? { icon: <MDBIcon fas icon="circle-notch" spin/> } : false}
                  disabled={pendingRequest || !topic || Object.keys(props.socialAccounts).filter(key => props.socialAccounts[key]).length === 0}
                  onClick={handleSubmit}
                  iconPosition="end"
                >
                  {pendingRequest ? t('newPost.buttonPendingGeneratePost') : t('newPost.buttonGeneratePost')}
                </Button>
              </Col>
            </Row>
          </>
        }

      </form>
    </>
  );
};

export default FormBuilder;
